import React, { useState } from 'react';
import { productList } from '../../data/products';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';

const ProductSelect = ({ selectedProducts = [], onChange }) => {
  const [newProduct, setNewProduct] = useState({
    id: '',
    name: '',
    quantity: 1,
    unit: 'kg',
    unitPrice: 0
  });

  const handleProductChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'id') {
      const selected = productList.find(p => p.id === value);
      if (selected) {
        setNewProduct({
          ...newProduct,
          id: selected.id,
          name: selected.name,
          unit: selected.unit
        });
      }
    } else if (name === 'quantity' || name === 'unitPrice') {
      // Converter para número quando for quantity ou unitPrice
      const numericValue = parseFloat(value) || 0;
      setNewProduct({
        ...newProduct,
        [name]: numericValue
      });
    } else {
      setNewProduct({
        ...newProduct,
        [name]: value
      });
    }
  };

  const addProduct = () => {
    if (!newProduct.id || newProduct.quantity <= 0 || newProduct.unitPrice <= 0) {
      alert('Por favor, preencha todos os campos do produto corretamente.');
      return;
    }

    const updatedProducts = [
      ...selectedProducts,
      {
        ...newProduct,
        // Garantir que quantity e unitPrice sejam números
        quantity: Number(newProduct.quantity),
        unitPrice: Number(newProduct.unitPrice),
        totalPrice: Number(newProduct.quantity) * Number(newProduct.unitPrice)
      }
    ];

    onChange(updatedProducts);
    
    // Reset form
    setNewProduct({
      id: '',
      name: '',
      quantity: 1,
      unit: 'kg',
      unitPrice: 0
    });
  };

  const removeProduct = (index) => {
    const updatedProducts = [...selectedProducts];
    updatedProducts.splice(index, 1);
    onChange(updatedProducts);
  };

  const calculateTotal = () => {
    return selectedProducts.reduce((total, product) => {
      const productTotal = Number(product.totalPrice) || 0;
      return total + productTotal;
    }, 0);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">Produtos</h2>
      
      <div className="space-y-6">
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <h3 className="font-medium text-gray-700">Adicionar Produto</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Produto"
              id="id"
              name="id"
              value={newProduct.id}
              onChange={handleProductChange}
              options={productList.map(p => ({ 
                value: p.id, 
                label: p.name 
              }))}
              placeholder="Selecione um produto"
            />
            
            <div className="flex gap-3">
              <div className="flex-1">
                <Input
                  label="Quantidade"
                  id="quantity"
                  name="quantity"
                  type="number"
                  min="1"
                  value={newProduct.quantity}
                  onChange={handleProductChange}
                />
              </div>
              
              <div className="w-20">
                <Input
                  label="Unidade"
                  id="unit"
                  name="unit"
                  value={newProduct.unit}
                  onChange={handleProductChange}
                  disabled
                />
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Preço Unitário (R$)"
              id="unitPrice"
              name="unitPrice"
              type="number"
              step="0.01"
              min="0"
              value={newProduct.unitPrice}
              onChange={handleProductChange}
            />
            
            <div className="flex items-end">
              <Button 
                type="button" 
                onClick={addProduct}
                className="mt-auto"
              >
                Adicionar
              </Button>
            </div>
          </div>
        </div>
        
        {selectedProducts.length > 0 ? (
          <div className="space-y-4">
            <h3 className="font-medium text-gray-700">Produtos Selecionados</h3>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Produto
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Quantidade
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Preço Unit.
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedProducts.map((product, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.quantity} {product.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        R$ {Number(product.unitPrice || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        R$ {Number(product.totalPrice || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td colSpan="3" className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      Valor total da mercadoria:
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                      R$ {calculateTotal().toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500">
            Nenhum produto selecionado. Adicione produtos usando o formulário acima.
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSelect;